import type { ServiceRequestResult } from '../base-service';
import { ServiceWithSession } from '../base-service';
import type { FormEntryIndex } from '@cognitoforms/types/server-types/forms/model/form-entry-index';
import type { FormsModel } from 'src/framework/forms-model';
import type { EntryViewFilter } from '@cognitoforms/types/server-types/forms/model/entry-view-filter';
import type { ObjectLookup } from 'src/util';
import type { DB, Transaction } from 'idb';
import { openDb } from 'idb';
import type { Order } from '@cognitoforms/types/server-types/payment/model/order';
import { IndexType } from '@cognitoforms/types/server-types/forms/model/index-type';
import type { FormSession } from '../form-session';
import type { EntryData, EntityJson, UserSpecificViewSettings } from './entry-view-types';
import { Deferred } from 'src/util/deferred';
import { getLookupManager } from 'src/framework/model/extensions/lookup-field/lookup-manager';
import { getCascadeFilterManager } from 'src/framework/model/extensions/lookup-field/cascade-filter-manager';
import type Log from 'src/framework/logging/log';
import { isEncryptedRecord } from './encryption';
import type * as Encryption from './encryption';
import type { EntryLookupViewModel } from '@cognitoforms/types/server-types/services/view-models/entry-lookup-view-model';
import type { EntryKey } from '@cognitoforms/types/server-types/forms/services/entry-key';
import type { FormEntry } from '@cognitoforms/types/server-types/forms/model/form-entry';
import type { FallbackEntryLookupRequest } from '@cognitoforms/types/server-types/services/controllers/entry-lookup-controller+-fallback-entry-lookup-request';
import type { EntityOfType } from '@cognitoforms/model.js';
import type { EntryLookupKey } from '@cognitoforms/types/server-types/services/controllers/entry-lookup-key';

const BATCH_TIME = 1;

/**
 * Controls how many entries can be loaded by a single network request when loading non-lookup entries.
 */
const BATCH_SIZE = 100;

/**
 * Controls how many requests for entry batches can be made for a particular view.
 */
const MAX_CONCURRENT_BATCH_REQUESTS = 3;

type LoadEntryDataBatch = {
	id: string;
	entries: Map<string, EntryKey | EntryLookupKey>;
	request: Promise<Map<string, EntryData>>;
}

type LookupEntriesResponse = {
	entries: EntryData[];
	shouldRetry: string[];
	recordLevelSecurity?: boolean;
};

type FormEntryIndexRecord = {
	Id: string;
	entries: FormEntryIndex[];
	timestamp: string;
}

export class EntryLookupBatch {
	private _entryViewMap: Map<string, Set<string>> = new Map();
	private _viewTokens: Record<string, string> = {};
	private _request: Promise<EntryData[]>;

	constructor(request: Promise<EntryData[]>, viewTokens: Record<string, string>) {
		this._request = request;
		this._viewTokens = viewTokens;
	}

	get idList() {
		return Array.from(this._entryViewMap.keys());
	}

	get entryLookupViewModel(): EntryLookupViewModel {
		const entryKeys: EntryLookupKey[] = [];
		const requestedViewTokens: string[] = [];
		for (const [entryId, views] of this._entryViewMap) {
			const requestedViewIndexes = [];
			for (const view of views) {
				const token = this._viewTokens[view];
				if (!token)
					continue;

				let index = requestedViewTokens.indexOf(token);
				if (index === -1) {
					index = requestedViewTokens.push(token) - 1;
				}

				requestedViewIndexes.push(index);
			}

			entryKeys.push({ EntryId: entryId, Views: Array.from(requestedViewIndexes) } as EntryLookupKey);
		}

		return {
			Tokens: requestedViewTokens,
			Entries: entryKeys
		};
	}

	addEntryIds(view: string, ids: string[]) {
		for (const id of ids) {
			let viewSet = this._entryViewMap.get(id);
			if (!viewSet) {
				viewSet = new Set<string>();
				this._entryViewMap.set(id, viewSet);
			}

			viewSet.add(view);
		}
	}

	getEntries(ids: string[]): Promise<EntryData[]> {
		return this._request.then(entries => {
			const relevantIds = new Set(ids);
			return entries.filter(e => relevantIds.has(e.Id));
		});
	}
}

let _id = 0;
let crypto: typeof Encryption;
const cryptoKeys: ObjectLookup<CryptoKey> = {};

type DbStoreName = 'FormEntry' | 'FormEntryIndex';

export default class EntryViewService extends ServiceWithSession {
	readonly id = _id++;
	readonly model: FormsModel;
	private readonly log: Log;
	private db: Promise<DB> = Promise.resolve(null);
	private readonly sessionViewTokens: Readonly<Record<string, string>>;
	private additionalViewTokens: Record<string, string>;
	private encryptionSeed: ArrayBuffer;

	private loadingPromises = new Set<Promise<void>>();
	private entryLookupBatch: EntryLookupBatch;
	private loadEntryBatches: ObjectLookup<LoadEntryDataBatch> = {};

	constructor(model: FormsModel, session: FormSession, log: Log, pseudoLoadDeleted = false, suppressLookupLoadingErrors = false) {
		super(session);
		this.model = model;
		this.log = log;
		this.sessionViewTokens = session.viewTokens;
		const entryTypeNames = model.entryTypeName.split('.');
		const orgName = entryTypeNames[entryTypeNames.length - 2];

		if (session.flags.entryCaching) {
			this.openIndexedDB(orgName, model.version + 1);
		}

		model.enableLookups(this, getLookupManager, getCascadeFilterManager, log, pseudoLoadDeleted, suppressLookupLoadingErrors);
	}

	private get viewTokens() {
		if (!this.additionalViewTokens)
			return this.sessionViewTokens;
		return Object.assign({}, this.sessionViewTokens, this.additionalViewTokens);
	}

	public registerViewToken(viewId: string, token: string) {
		if (!this.additionalViewTokens)
			this.additionalViewTokens = {};
		this.additionalViewTokens[viewId] = token;
	}

	private async openIndexedDB(orgName: string, version: number) {
		const onFail = () => this.log.warn('Failed to open indexed DB.');
		try {
			this.db = openDb(`Cognito.Forms.${orgName}`, version, upgrade => {
				if (upgrade.objectStoreNames.contains('FormKey'))
					upgrade.deleteObjectStore('FormKey');
				if (upgrade.objectStoreNames.contains('FormEntry'))
					upgrade.deleteObjectStore('FormEntry');
				if (upgrade.objectStoreNames.contains('FormEntryIndex'))
					upgrade.deleteObjectStore('FormEntryIndex');

				upgrade.createObjectStore('FormKey', { keyPath: 'Form' });
				upgrade.createObjectStore('FormEntry', { keyPath: 'Id' });
				upgrade.createObjectStore('FormEntryIndex', { keyPath: 'Id' });
			}).then(async db => {
				(db as any)._db.onversionchange = (e) => {
					db.close();
					this.openIndexedDB(orgName, e.newVersion);
				};

				if (this.session.encryptionKey) {
					crypto = await import('./encryption');

					this.encryptionSeed = await crypto.isCryptoSupported(this.session.encryptionKey);
					return this.encryptionSeed ? db : null;
				}
				else
					return db;
			}).catch(e => {
				onFail();
				return null;
			});
		}
		catch {
			onFail();
			this.db = Promise.resolve(null);
		}
	}

	async cacheRecord<T>(formId: string, storeName: DbStoreName, record: T) {
		const db = await this.db;
		if (db) {
			try {
				const keyPath = db.transaction(storeName, 'readonly').objectStore(storeName).keyPath as string;
				const encrypted = { [keyPath]: record[keyPath], ...await this.encrypt(formId, record) };
				return db.transaction(storeName, 'readwrite').objectStore(storeName).put(encrypted);
			}
			catch (e) {
				this.log.warn(e);
				this.db = Promise.resolve(null);
				return null;
			}
		}
	}

	async loadRecord<T>(formId: string, storeName: DbStoreName, recordKey: any, transaction: Transaction = null): Promise<T> {
		return this.executeWithTracking(async () => {
			const db = await this.db;
			if (db) {
				try {
					if (!transaction)
						transaction = db.transaction(storeName, 'readonly');
					return this.decrypt(formId, await transaction.objectStore(storeName).get(recordKey));
				}
				catch (e) {
					this.log.warn(e);
					this.db = Promise.resolve(null);
					return null;
				}
			}
		});
	}

	private getCacheKey(view: string, userShort: string, roleId: number): string {
		if (!userShort)
			return view;

		return `${view}|${userShort}|${roleId}`;
	}

	async loadCachedIndexes(formId: string, view: string, userSpecificSettings?: UserSpecificViewSettings): Promise<FormEntryIndexRecord> {
		const cachedIndexes = await this.loadRecord<FormEntryIndexRecord>(formId, 'FormEntryIndex', this.getCacheKey(view, userSpecificSettings?.userShort, userSpecificSettings?.roleId));

		// Discard timestamps erroneously set to client-time
		// TODO: Remove when we are confident that all timestamps originating from client are discarded
		if (cachedIndexes?.timestamp?.indexOf('Z') > -1)
			cachedIndexes.timestamp = '';

		return cachedIndexes;
	}

	async cacheIndexes(
		formId: string,
		view: string,
		indexes: FormEntryIndex[],
		timestamp: string,
		userSpecificSettings?: UserSpecificViewSettings
	): Promise<void> {
		await this.cacheRecord<FormEntryIndexRecord>(formId, 'FormEntryIndex', {
			entries: indexes,
			timestamp,
			Id: this.getCacheKey(view, userSpecificSettings?.userShort, userSpecificSettings?.roleId)
		});
	}

	private async encrypt<T>(formId: string, data: T) {
		if (this.encryptionSeed)
			return crypto.encrypt(data, await this.getCryptoKey(formId), this.encryptionSeed) as any;
		return data;
	}

	private async decrypt<T>(seed: string, data: T) {
		if (data && isEncryptedRecord(data)) {
			if (this.encryptionSeed)
				return (await crypto.decrypt(data, await this.getCryptoKey(seed), this.encryptionSeed)) as T;
			else
				return null;
		}
		return data;
	}

	private async getCryptoKey(formId: string) {
		// See if the key has already been loaded
		let key = cryptoKeys[formId];
		try {
			// Otherwise, attempt to load from db
			if (!key) {
				const db = await this.db;
				if (db) {
					const formKey = await db.transaction('FormKey', 'readonly').objectStore('FormKey').get(formId) as any;

					// Key found in database cache
					if (formKey)
						key = formKey.Key;
					// Create and cache a new encryption key
					else {
						key = await window.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
						await db.transaction('FormKey', 'readwrite').objectStore('FormKey').put({ Form: formId, Key: key });
					}

					cryptoKeys[formId] = key;
				}
			}
		}
		catch (e) {
			this.log.warn(e);
			this.db = Promise.resolve(null);
		}

		return key;
	}

	private isLookupView(viewId: string) {
		return this.viewTokens[viewId]?.startsWith('l-');
	}

	async getIndex(
		formId: string,
		view: string,
		userSpecificSettings?: UserSpecificViewSettings,
		timestamp?: string,
		progressCallback?: (progress: number, interval: number) => void
	): Promise<FormEntryIndex[]> {
		if (!this.isValidView(view))
			return Promise.resolve([]);

		return this.executeWithTracking(async () => {
			// Prevent parallel index building
			let delay = 100;
			let indexes: FormEntryIndex[];
			let result: ServiceRequestResult;
			let errors = 0;

			while (true) {
				// Allow up to 5 failures before cancelling
				result = await this.serviceRequest({
					method: 'get',
					// lookups should use the public index endpoint, regular views use admin
					endpoint: `forms/${this.isLookupView(view) ? 'public' : 'admin'}/${formId}/entries/index/${this.viewTokens[view]}`,
					params: { timestamp: timestamp, poll: true, roleId: userSpecificSettings?.roleId }
				});

				if (result.error) {
					if (++errors >= 5)
						break;
					continue;
				}

				if (result.response && result.response.data) {
					indexes = result.response.data as FormEntryIndex[];
					const lck = indexes.find(idx => idx.Type === IndexType.Lock);
					if (lck) {
						if (progressCallback) progressCallback(lck.PercentDone * 100, delay);

						await new Promise(resolve => {
							setTimeout(resolve, delay);
						});
						// If this is a checkpoint lock, poll faster
						if (lck.Id)
							delay = 100;
						else
							delay = delay * 2 > 10000 ? 10000 : delay * 2;
						continue;
					}
				}
				if (progressCallback) progressCallback(100, -500);
				break;
			}

			if (indexes) {
				return this.ensureBatchesLoaded(indexes);
			}

			throw this.parseError(result.error);
		});
	}

	async getSpecificIndex(
		entryId: string,
		formId: string,
		view: string,
		userSpecificSettings?: UserSpecificViewSettings,
		timestamp?: string
	): Promise<FormEntryIndex[]> {
		if (!this.isValidView(view))
			return Promise.resolve([]);

		return this.executeWithTracking(async () => {
			// Prevent parallel index building
			let pollCount = 0;
			const delay = 100;
			let indexes: FormEntryIndex[];
			let result: ServiceRequestResult;
			while (pollCount++ < 4) {
				result = await this.serviceRequest({
					method: 'get',
					endpoint: `forms/admin/${formId}/entries/index/${entryId}/${this.viewTokens[view]}`,
					params: { timestamp: timestamp, poll: true, roleId: userSpecificSettings?.roleId }
				});
				if (result.response?.data) {
					const index = result.response.data as FormEntryIndex;
					indexes = index ? [index] : [];
					break;
				}
				else {
					await new Promise(resolve => {
						setTimeout(resolve, delay);
					});
				}
			}
			if (indexes) {
				return this.ensureBatchesLoaded(indexes);
			}

			throw this.parseError(result.error);
		});
	}

	async ensureBatchesLoaded(indexes: FormEntryIndex[]): Promise<FormEntryIndex[]> {
		// Load indexes from file storage
		let batches: FormEntryIndex[] = [];
		let lastCheckpoint = 0;
		for (let i = 0; i < indexes.length; i++) {
			const index = indexes[i];
			if (index.Type === IndexType.Checkpoint) {
				batches = [];
				lastCheckpoint = i;
			}

			if (index.Batch && index.Batch.StorageUrl)
				batches.push(index);
		}

		// Only worry about indexes after the most recent checkpoint
		if (lastCheckpoint)
			indexes = indexes.slice(lastCheckpoint);

		// If indexes need to be fetched from file storage, merge them into the existing array
		const requests = batches.map(batch => this.client.get<FormEntryIndex[]>(batch.Batch.StorageUrl));
		if (requests.length) {
			const responses = await Promise.all(requests);
			let responseIndex = responses.length - 1;
			for (let insertionIndex = indexes.length - 1; insertionIndex >= 0; insertionIndex--) {
				if (indexes[insertionIndex].Batch && indexes[insertionIndex].Batch.StorageUrl) {
					const toInsert = responses[responseIndex].data;
					for (let batchIndex = toInsert.length - 1; batchIndex >= 0; batchIndex--) {
						indexes.splice(insertionIndex + 1, 0, toInsert[batchIndex]);
					}
					responseIndex--;
				}
			}
		}

		return indexes;
	}

	async getFilter(formId: string, filter: EntryViewFilter) {
		const result = await this.serviceRequest({
			method: 'post',
			endpoint: `svc/forms/${formId}/entry-views/filter`,
			isCognitoJson: true,
			data: filter
		});

		if (result.response && result.response.data)
			return result.response.data;

		throw this.parseError(result.error);
	}

	async lookupReferencedEntries(rootEntry: EntityOfType<FormEntry>, ids: string[]): Promise<EntryData[]> {
		return this.executeWithTracking(async () => {
			const fetchIds = new Set(ids);

			// Try to load entries from cache
			const cachedEntryData = (await this.getCachedEntries(ids)).filter(e => !!e) as EntryData[];

			// Separate cached entries from uncached
			cachedEntryData.forEach(e => fetchIds.delete(e.Id));

			const operationName = 'Lookup Entries Fallback';
			this.log.startTrackEvent(operationName);

			const result = await this.serviceRequest({
				method: 'post',
				endpoint: 'svc/entry-lookup/fallback',
				data: {
					FormId: rootEntry.Form.Id,
					RootEntryToken: (rootEntry as any).$entryToken,
					RootEntryId: (rootEntry as any).$rootEntryId || rootEntry.Id,
					RootViewTokens: (rootEntry as any).$viewTokens,
					RootViewId: (rootEntry as any).$viewId,
					RootEntryChange: (rootEntry as any).$entryChange,
					LookupEntryKeys: Array.from(fetchIds.values())
						.map(id => ({ EntryId: id } as EntryKey))
						// send etag to see if cached entries are outdated
						.concat(cachedEntryData.map(e => ({ EntryId: e.Id, EntryETag: e.$etag } as EntryKey)))
				} satisfies FallbackEntryLookupRequest
			});

			if (result.response && result.response.data) {
				const { entries } = await this.handleEntryLookupResponse(result, cachedEntryData);
				entries.forEach(e => {
					e['$rootEntryId'] = rootEntry.Id;
					e['$entryToken'] = (rootEntry as any).$entryToken;
					e['$viewTokens'] = (rootEntry as any).$viewTokens;
					e['$viewId'] = (rootEntry as any).$viewId;
					e['$entryChange'] = (rootEntry as any).$entryChange;
				});
				const fieldsPerForm = entries.map(e => [e.Form.Id, Object.keys(e).length]);
				this.log.stopTrackEvent(operationName, {
					numSourceFormFields: JSON.stringify(Object.fromEntries(fieldsPerForm)).toString(),
					numberLoaded: fetchIds.size.toString()
				});
				return entries;
			}

			throw this.parseError(result.error);
		});
	}

	private async handleEntryLookupResponse(result: ServiceRequestResult, cachedEntryData: EntryData[]): Promise<LookupEntriesResponse> {
		const data = result.response.data as { recordLevelSecurity?: boolean, orders?: EntityJson<Order>[]; entries: EntityJson<EntryData>[]; };
		const entries: EntryData[] = [];
		const invalidatedEntryIds = new Set<string>();

		const orders = new Map<string, EntityJson<Order>>((data.orders || []).map(o => [o.json.Id, o]));

		const notInView: string[] = [];
		for (const entryData of data.entries) {
			if (entryData.json) {
				const entry = entryData.json;
				if (entry.Entry.Order && orders.has(entry.Entry.Order.Id))
					entry.Order = orders.get(entry.Entry.Order.Id).json;

				entry.$etag = entryData.etag;
				entries.push(entry);
				invalidatedEntryIds.add(entry.Id);
			}
			else {
				console.warn('Unable to resolve lookup value:', entryData.id, entryData.visibility);
				invalidatedEntryIds.add(entryData.id);
				if (entryData.visibility === 'NotInView')
					notInView.push(entryData.id);
			}
		}

		// Cache entries
		await Promise.all(entries.map(e => this.cacheRecord(e.Form.Id, 'FormEntry', e)));

		// Return a combination of entries from server, and cached entries that were not expired
		return {
			recordLevelSecurity: data.recordLevelSecurity,
			entries: entries.concat(cachedEntryData.filter(e => !invalidatedEntryIds.has(e.Id))),
			shouldRetry: notInView
		};
	}

	async lookupEntries(view: string, ids: string[], rootEntry: EntityOfType<FormEntry> | null): Promise<EntryData[]> {
		if (!this.isValidView(view))
			return Promise.resolve([]);

		if (!this.entryLookupBatch) {
			const request = new Deferred<EntryData[]>();
			const batch = this.entryLookupBatch = new EntryLookupBatch(request.promise, this.viewTokens);
			setTimeout(async () => {
				this.entryLookupBatch = undefined;
				const { entries, shouldRetry } = await this.lookupEntriesInBatch(batch);
				if (rootEntry && shouldRetry.length > 0) {
					try {
						const fallbackEntries = await this.lookupReferencedEntries(rootEntry, shouldRetry);
						const missingEntries = shouldRetry.filter(id => !fallbackEntries.find(e => e.Id === id));
						if (missingEntries.length > 0) {
							this.log.trace('Rejected entry lookup fallback request for one or more entries.', {
								Source: 'entry-view-service',
								EntryId: rootEntry.Id,
								Unloaded: missingEntries.join(',')
							});
						}
						request.resolve(entries.concat(fallbackEntries));
					}
					catch (e) {
						request.reject(e);
					}
				}
				else
					request.resolve(entries);
			}, BATCH_TIME);
			this.executeWithTracking(() => request.promise);
		}

		this.entryLookupBatch.addEntryIds(view, ids);

		return this.entryLookupBatch.getEntries(ids);
	}

	private lookupEntriesInBatch(batch: EntryLookupBatch): Promise<LookupEntriesResponse> {
		return this.executeWithTracking(async () => {
			const lookupViewModel = batch.entryLookupViewModel;

			// Try to load entries from cache
			const cachedEntryData = (await this.getCachedEntries(batch.idList)).filter(e => !!e) as EntryData[];

			// Send etag to see if cached entries are outdated
			cachedEntryData.forEach(e => {
				const entryKey = lookupViewModel.Entries.find(ek => ek.EntryId === e.Id);
				entryKey.EntryETag = e.$etag;
			});

			const operationName = 'Lookup Entries in Batch';
			this.log.startTrackEvent(operationName);

			const result = await this.serviceRequest({
				method: 'post',
				endpoint: 'svc/entry-lookup',
				data: lookupViewModel
			});

			if (result.response && result.response.data) {
				const { entries, shouldRetry, recordLevelSecurity } = await this.handleEntryLookupResponse(result, cachedEntryData);
				entries.forEach(e => {
					const entryKey = lookupViewModel.Entries.find(ek => ek.EntryId === e.Id);
					if (entryKey)
						e['$viewTokens'] = entryKey.Views.map(viewIndex => lookupViewModel.Tokens[viewIndex]);
				});
				const fieldsPerForm = entries.map(e => [e.Form.Id, Object.keys(e).length]);
				this.log.stopTrackEvent(operationName, {
					numSourceFormFields: JSON.stringify(Object.fromEntries(fieldsPerForm)).toString(),
					numberLoaded: lookupViewModel.Entries.length.toString(),
					recordLevelSecurity: recordLevelSecurity === true ? 'True' : 'False'
				});
				return { entries, shouldRetry };
			}

			throw this.parseError(result.error);
		});
	}

	private async getCachedEntries(ids: string[]): Promise<EntryData[]> {
		const formId = ids[0].split('-')[0];
		const db = await this.db;
		if (db) {
			try {
				const transaction = (await this.db).transaction('FormEntry', 'readonly');
				return Promise.all(ids.map(id => this.loadRecord<EntryData>(formId, 'FormEntry', id, transaction)));
			}
			catch (e) {
				this.log.warn(e);
				this.db = Promise.resolve(null);
				return [];
			}
		}
		else
			return [];
	}

	async loadEntryData(view: string, isAdmin: boolean, ...entries: EntryKey[]): Promise<EntryData[]> {
		if (this.isLookupView(view))
			return this.lookupEntries(view, entries.map(entryKey => entryKey.EntryId), null);

		if (!this.isValidView(view))
			return [];

		let batch = this.loadEntryBatches[view];

		if (!batch) {
			const request = new Deferred();
			batch = { entries: new Map(), request: request.promise, id: view };

			setTimeout(() => {
				delete this.loadEntryBatches[view];
				request.resolve(this.loadEntryDataInBatch(batch, isAdmin)
					.then(entryData => new Map(entryData.map(d => [d.Id, d]))));
			});

			this.executeWithTracking(() => request.promise);
		}

		this.loadEntryBatches[view] = batch;

		for (const key of entries)
			batch.entries.set(key.EntryId, key);

		const dataMap = await batch.request;
		const entryData = entries.map(e => dataMap.get(e.EntryId));
		for (const entry of entryData)
			(entry as any).$viewTokens = [this.viewTokens[view]];
		return entryData;
	}

	private loadEntryDataInBatch(batch: LoadEntryDataBatch, isAdmin: boolean = false): Promise<EntryData[]> {
		return this.executeWithTracking(async () => {
			const indexMap = batch.entries;

			let cachedEntryData = await this.getCachedEntries(Array.from(indexMap.keys()));

			// Entries with out of date etags need to be reloaded
			cachedEntryData = cachedEntryData.filter((entry: EntryData) => {
				if (entry && entry.$etag === indexMap.get(entry.Id).EntryETag) {
					indexMap.delete(entry.Id);
					return true;
				}
				return false;
			}) as EntryData[];

			// All requested entries were found in cache, no web request needed
			if (indexMap.size === 0)
				return cachedEntryData;

			const loadedData: EntryData[] = [];

			let numRequested = 0;
			const requestQueue = [];
			const keysToLoad = Array.from(indexMap.values());

			while (numRequested < keysToLoad.length) {
				if (requestQueue.length === MAX_CONCURRENT_BATCH_REQUESTS) {
					// wait for a spot in the queue to open up
					await Promise.race(requestQueue);
					continue;
				}

				const currentKeyBatch = keysToLoad.slice(numRequested, (numRequested += BATCH_SIZE));

				const request = (async () => {
					const result = await this.serviceRequest({
						method: 'post',
						endpoint: 'forms/admin/entrybatch',
						data: { Token: this.viewTokens[batch.id], Entries: currentKeyBatch }
					});

					if (result.response && result.response.data) {
						const data = result.response.data as { orders?: Order[], entries: EntryData[] };
						const orders = new Map<string, Order>((data.orders || []).map(o => [o.Id, o]));
						// Cache entries
						await Promise.all(data.entries.map(entry => {
							if (entry.Entry.Order && orders.has(entry.Entry.Order.Id))
								entry.Order = orders.get(entry.Entry.Order.Id);
							entry.$etag = indexMap.get(entry.Id).EntryETag;

							return this.cacheRecord(entry.Form.Id, 'FormEntry', entry);
						}));

						loadedData.push(...data.entries);
					}
					else
						throw this.parseError(result.error);

					requestQueue.splice(requestQueue.indexOf(request), 1);
				})();
				requestQueue.push(request);
			}

			// once we're past the loop, we've requested everything we need, but may still need to wait on the last tasks in the queue
			await Promise.all(requestQueue);

			return loadedData.concat(cachedEntryData);
		});
	}

	private executeWithTracking<T>(action: () => Promise<T>) {
		const task = new Deferred();
		this.loadingPromises.add(task.promise);
		const result = action();
		const cleanupTask = () => {
			this.loadingPromises.delete(task.promise);
			task.resolve();
		};
		result.then(cleanupTask, cleanupTask);
		return result;
	}

	get loadingComplete(): Promise<void> {
		return new Promise(async resolve => {
			while (this.loadingPromises.size) {
				await Promise.all(this.loadingPromises);
				await new Promise<void>(resolve => setTimeout(resolve));	// allow anything awaiting loading to queue more tasks
			}
			resolve();
		});
	}

	isValidView(viewId: string) {
		return !!this.viewTokens[viewId];
	}
}